import { ChoiceDetails } from 'menu/components/MenuModal/ModalComponents/ChoiceDetails';
import { MultipleChoice } from 'menu/components/MenuModal/ModalComponents/MultipleChoice';
import { SingleChoice } from 'menu/components/MenuModal/ModalComponents/SingleChoice';
import {
  countChoiceGroupSelectionQuantities,
  doesPortionHaveValidPrice,
  getChildPortionId,
  getChoiceGroupType,
  getPriceForSupplement,
} from 'menu/utils';
import { Choice, ChoiceGroup, ChoiceType } from 'types/models';

interface MenuChoiceSelectorProps {
  choice: Choice;
  choiceGroup: ChoiceGroup;
  choiceGroupIndex: number;
  choiceIndex: number;
  isNestedChoice: boolean;
  numberOfChoices: number;
  portionId: number;
  updateChoice: (
    choiceIndex: number,
    choiceGroupIndex: number,
    quantity?: number,
    hasNestedChoice?: boolean,
  ) => void;
}

export const MenuChoiceSelector: React.FC<MenuChoiceSelectorProps> = ({
  choice,
  choiceGroup,
  choiceGroupIndex,
  choiceIndex,
  isNestedChoice,
  numberOfChoices,
  portionId,
  updateChoice,
}) => {
  const productToUse = choice?.productToUse;
  const portionIdToUse: number = getChildPortionId(
    choiceGroup.portionRatios,
    choice.choiceId ?? choice.productId,
    portionId,
  );
  const portion = productToUse
    ? productToUse.portions[portionIdToUse]
    : undefined;

  const quantity = choice.quantity ?? 0;
  const isSelected = quantity > 0;
  const minChoices = choiceGroup.minimum;
  const maxChoices = choiceGroup.maximum;
  const choiceDisplayRecord = choice.displayRecordToUse;

  const totalSelectionQuantity = countChoiceGroupSelectionQuantities(
    choiceGroup?.choices,
  );
  const choiceGroupMaxReached =
    maxChoices !== undefined && totalSelectionQuantity >= maxChoices;

  // // get maximum we can select for this item without pushing the choicegroup over the limit
  const sumOfOtherSelectionQuantities = totalSelectionQuantity - quantity;
  const maxForSelection = choiceGroup.maximum - sumOfOtherSelectionQuantities;

  const calculateDisabled = (): boolean => {
    return !isSelected && choiceGroupMaxReached && choiceGroup?.maximum > 1;
  };

  const calculateOutOfStock = () => {
    if (productToUse) {
      const { portions } = productToUse;

      // If it hasn't been disabled yet, check if the portion exists. if it does not then disable it.
      return (
        portions[portionIdToUse] === undefined ||
        Boolean(productToUse.isOutOfStock) ||
        !hasValidPrice()
      );
    }
    return false;
  };

  const hasValidPrice = (): boolean => {
    if (choice.productId) {
      const isDefault = choiceGroup.defaults.includes(choice.productId);
      // if a default or if inclusive range is same as max limit
      // these products will be charged at a supplement price
      if (
        isDefault ||
        (choiceGroup.inclusive &&
          choiceGroup.inclusive > 0 &&
          choiceGroup.inclusive === choiceGroup.maximum)
      ) {
        return true;
      }

      // else check that the portion has a price
      return portion ? doesPortionHaveValidPrice(portion) : false;
    }
    return true;
  };

  const calculateHasChoices = (): boolean => {
    if (productToUse) {
      const { portions } = productToUse;
      // if portion in the menu has choice groups, and those choice groups aren't empty, then we have choices
      return portions[portionIdToUse]?.choices?.length > 0;
    }
    // if choice doesn't have a product ID then it's a sub-choice group so of course...
    return true;
  };
  const priceToDisplay = portion
    ? getPriceForSupplement(
        choice,
        choiceGroup,
        portion,
        totalSelectionQuantity,
      )
    : 0;

  const disabled = calculateDisabled();
  const outOfStock = calculateOutOfStock();
  const hasChoices = calculateHasChoices();
  const choiceType = getChoiceGroupType(minChoices, maxChoices);
  const hasNestedChoice = hasChoices && !isSelected;
  const isUnsupportedNestedChoice = hasNestedChoice && isNestedChoice;

  // Defaults are set a quantity=1 when the product is first clicked
  // Here we check if it is disabled or out of stock to set the quantity=0
  if (isSelected && (disabled || outOfStock)) {
    updateChoice(choiceIndex, choiceGroupIndex, 0);
  }

  if (
    choiceType === ChoiceType.SingleRequired ||
    choiceType === ChoiceType.SingleOptional
  ) {
    return (
      <SingleChoice
        choiceIndex={choiceIndex}
        choiceGroupIndex={choiceGroupIndex}
        numberOfChoices={numberOfChoices}
        updateChoice={updateChoice}
        disabled={disabled || isUnsupportedNestedChoice}
        outOfStock={outOfStock}
        isSelected={isSelected}
        choiceGroupId={choiceGroup.id}
        hasNestedChoice={hasNestedChoice}
        choiceType={choiceType}
      >
        <ChoiceDetails
          choiceDisplayRecord={choiceDisplayRecord}
          portion={portion}
          productToUse={productToUse}
          priceToDisplay={priceToDisplay}
        />
      </SingleChoice>
    );
  }
  if (choiceType === ChoiceType.Multiple) {
    return (
      <MultipleChoice
        quantity={quantity}
        updateChoice={updateChoice}
        choiceIndex={choiceIndex}
        choiceGroupIndex={choiceGroupIndex}
        hasNestedChoice={hasNestedChoice}
        hasChoices={hasChoices}
        maxForSelection={maxForSelection}
        disabled={disabled || isUnsupportedNestedChoice}
        outOfStock={outOfStock}
      >
        <ChoiceDetails
          choiceDisplayRecord={choiceDisplayRecord}
          portion={portion}
          productToUse={productToUse}
          priceToDisplay={priceToDisplay}
        />
      </MultipleChoice>
    );
  }
  return null;
};
